import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Menu, RefreshCw, CheckCircle2, AlertTriangle, Users } from "lucide-react";
import { authFetch } from "@/lib/authFetch";
import { startLinkJob } from "@/lib/linkJobs";
import { useTheme } from "@/context/ThemeContext";
import Sidebar from "@/components/Sidebar";
import { AffiliateNav } from "@/components/AffiliateNav";
import { CandidatePicker, LinkedClientsDialog, RoleTag } from "@/components/LinkedClientsDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const TYPES = [
  ["inactive", "Inactive user", "A client is linked to someone whose login is inactive."],
  ["hidden_lender", "Hidden lender", "A client's Affiliated Lender is hidden on the referral forms."],
  ["unknown", "Unknown name", "A Lender, Affiliate 1 or Affiliate 2 name doesn't match any affiliate in the portal."],
  ["duplicate", "Duplicate affiliate", "Two or more affiliate records look like the same person."],
];

// The changes a fix makes, in the shape the link-changes endpoint expects.
const buildItems = (p, choice) => {
  if (p.type === "duplicate") {
    const keep = p.records.find((r) => r.user_id === choice);
    return p.records.filter((r) => r.user_id !== choice).flatMap((r) => Object.entries(r.roles)
      .filter(([role]) => role !== "lender" || keep.is_lender)
      .flatMap(([role, ids]) => ids.map((client_id) => ({ client_id, role, from_user_id: r.user_id, new_user_id: choice }))));
  }
  return p.clients.map((c) => ({ client_id: c.client_id, role: p.role, new_user_id: choice.user_id,
    ...(p.type === "unknown" ? { from_value: p.value } : { from_user_id: p.person.user_id }) }));
};
const describe = (p, choice) => {
  if (p.type === "duplicate") {
    const keep = p.records.find((r) => r.user_id === choice);
    const n = new Set(buildItems(p, choice).map((i) => i.client_id)).size;
    return `Keep ${keep.name} (${keep.email}) and move ${n} client${n === 1 ? "" : "s"} from the other ${p.records.length - 1} record${p.records.length > 2 ? "s" : ""} onto it.`;
  }
  const from = p.type === "unknown" ? `“${p.label}”` : p.person.name;
  return `Replace ${from} with ${choice.name} (${choice.email}) as ${p.role === "lender" ? "Affiliated Lender" : p.role === "assigned_user" ? "Assigned User" : p.role.replace("affiliate_", "Affiliate ")} on ${p.count} client${p.count === 1 ? "" : "s"}.`;
};

const useConfirm = () => {
  const [state, setState] = useState(null);
  const res = useRef(null);
  const confirm = (opts) => new Promise((r) => { res.current = r; setState(opts); });
  const close = (v) => { res.current?.(v); setState(null); };
  const dialog = (
    <AlertDialog open={!!state} onOpenChange={(o) => !o && close(false)}>
      <AlertDialogContent data-testid="problem-confirm-dialog">
        <AlertDialogHeader><AlertDialogTitle>{state?.title}</AlertDialogTitle>
          <AlertDialogDescription asChild><div className="space-y-2 text-sm">{state?.body}</div></AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => close(false)} data-testid="problem-confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => close(true)} data-testid="problem-confirm-continue">{state?.ok || "Apply"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  return [confirm, dialog];
};

const Person = ({ p }) => p ? <span><b>{p.name}</b> <span className="text-slate-500">{p.email}{p.company ? ` · ${p.company}` : ""}</span></span> : null;

const ClientList = ({ clients }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="text-xs">
      <button className="underline text-slate-600" onClick={() => setOpen((v) => !v)}>{open ? "Hide clients" : `Show ${clients.length} client${clients.length === 1 ? "" : "s"}`}</button>
      {open && (
        <ul className="mt-1 max-h-40 overflow-y-auto grid sm:grid-cols-3 gap-x-3">
          {clients.map((c) => <li key={c.client_id}><Link to={`/admin/clients/${c.client_id}`} className="text-[#357c2a] hover:underline">{c.name}</Link></li>)}
        </ul>
      )}
    </div>
  );
};

const DuplicateRecords = ({ p, choice, setChoice, onOpenPerson }) => (
  <div className="space-y-1">
    {p.records.map((r) => (
      <label key={r.user_id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`dup-record-${r.user_id}`}>
        <input type="radio" name={p.id} checked={choice === r.user_id} onChange={() => setChoice(r.user_id)} />
        <Person p={r} /> · {r.clients} client{r.clients === 1 ? "" : "s"} {!r.is_active && <Badge variant="destructive">Inactive</Badge>}
        {choice === r.user_id && <Badge className="bg-emerald-100 text-emerald-800">Keep</Badge>}
        <button type="button" className="text-xs underline text-slate-600 ml-1" onClick={(e) => { e.preventDefault(); onOpenPerson(r); }} data-testid={`dup-record-open-${r.user_id}`}>Linked Clients</button>
      </label>
    ))}
    {!p.records.find((r) => r.user_id === choice)?.is_lender && p.records.some((r) => r.roles.lender?.length) && (
      <p className="text-xs text-amber-700">The kept record isn't a preferred lender, so Affiliated Lender links stay where they are.</p>
    )}
  </div>
);

const ProblemCard = ({ p, choice, setChoice, checked, onCheck, onApply, onDismiss, onOpenPerson, fixed }) => {
  const [picking, setPicking] = useState(false);
  return (
    <div className={`rounded-xl border p-4 space-y-2 transition-colors ${fixed ? "border-emerald-300 bg-emerald-50" : "border-amber-200 bg-amber-50/40"}`} data-testid={`problem-${p.id}`}>
      <div className="flex items-start gap-3">
        <Checkbox checked={checked} onCheckedChange={(v) => onCheck(!!v)} disabled={fixed} aria-label="Select problem" data-testid={`problem-check-${p.id}`} className="mt-1" />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {fixed ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
            <span className="font-medium text-sm">{p.title}</span>
            {p.role && <RoleTag role={p.role} />}
            {p.strength && <Badge variant="outline">{p.strength === "likely" ? "Likely" : "Possible"} · same {p.matched_on.join(", ")}</Badge>}
            {fixed && <Badge className="bg-emerald-600">Fix started</Badge>}
          </div>
          {p.person && <p className="text-xs"><Person p={p.person} /></p>}
          {p.clients && <ClientList clients={p.clients} />}
          {p.type === "duplicate" ? <DuplicateRecords p={p} choice={choice} setChoice={setChoice} onOpenPerson={onOpenPerson} /> : (
            <div className="text-sm space-y-2">
              <p data-testid={`problem-fix-${p.id}`}>
                <span className="text-slate-500">{choice && p.suggestion?.user_id === choice.user_id ? "Suggested fix: " : choice ? "Your choice: " : "No suggestion — "}</span>
                {choice ? <Person p={choice} /> : <span className="text-slate-500">choose a person{p.type === "hidden_lender" ? ", or keep as is" : ""}.</span>}
              </p>
              {picking && <CandidatePicker role={p.role} exclude={p.person?.user_id} value={choice} testId={`problem-candidate-${p.id}`} onChange={(c) => { setChoice(c); setPicking(false); }} />}
            </div>
          )}
          {!fixed && (
            <div className="flex flex-wrap gap-2 pt-1">
              <Button size="sm" onClick={onApply} disabled={!choice} data-testid={`problem-apply-${p.id}`}>Apply fix</Button>
              {p.type !== "duplicate" && <Button size="sm" variant="outline" onClick={() => setPicking((v) => !v)} data-testid={`problem-change-${p.id}`}>{picking ? "Close" : "Choose someone else"}</Button>}
              {p.person && (
                <Button size="sm" variant="ghost" onClick={() => onOpenPerson(p.person)} data-testid={`problem-open-${p.id}`}><Users className="h-4 w-4 mr-1" />Linked Clients</Button>
              )}
              <Button size="sm" variant="ghost" onClick={onDismiss} data-testid={`problem-dismiss-${p.id}`}>{p.type === "hidden_lender" ? "Keep as is" : "Not a problem"}</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function ProblemLinks() {
  const { isDarkMode } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [data, setData] = useState(null);
  const [type, setType] = useState("inactive");
  const [choices, setChoices] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [fixed, setFixed] = useState(new Set());
  const [person, setPerson] = useState(null);
  const [limit, setLimit] = useState(50);
  const [confirm, confirmDialog] = useConfirm();
  const typePicked = useRef(false);

  const load = async () => {
    setData(null);
    const res = await authFetch(`${API}/problem-links`, { credentials: "include" });
    if (!res.ok) { toast.error("Could not check links"); return; }
    const d = await res.json();
    setData(d);
    setChoices(Object.fromEntries(d.problems.map((p) => [p.id, p.type === "duplicate" ? p.suggestion.user_id : p.suggestion])));
    setSelected(new Set()); setFixed(new Set());
    if (!typePicked.current) {
      typePicked.current = true;
      setType((t) => (d.counts[t] ? t : TYPES.find(([k]) => d.counts[k])?.[0] || t));
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = (e) => { if (["problem_fix", "duplicate_merge"].includes(e.detail?.kind)) load(); };
    window.addEventListener("link-job-done", h);
    return () => window.removeEventListener("link-job-done", h);
  }, []);

  const list = useMemo(() => (data?.problems || []).filter((p) => p.type === type), [data, type]);

  const apply = async (ps) => {
    const ready = ps.filter((p) => choices[p.id]);
    if (!ready.length) { toast.error("Choose a person for the selected problems first"); return; }
    const body = ready.length === 1 ? <p>{describe(ready[0], choices[ready[0].id])}</p> : (
      <><p>Apply {ready.length} fixes:</p><ul className="list-disc pl-5 max-h-60 overflow-y-auto">{ready.map((p) => <li key={p.id}>{describe(p, choices[p.id])}</li>)}</ul></>
    );
    const skipped = ps.length - ready.length;
    if (!(await confirm({ title: "Apply fix?", body: <>{body}{skipped > 0 && <p className="text-amber-700">{skipped} selected without a chosen person will be left alone.</p>}<p>Saves to the CRM first; rejected clients stay as they are.</p></> }))) return;
    try {
      const items = ready.flatMap((p) => buildItems(p, choices[p.id]));
      if (!items.length) { toast.info("Nothing to move for this choice"); return; }
      await startLinkJob(items, type === "duplicate" ? "duplicate_merge" : "problem_fix",
        ready.length === 1 ? `Fixing: ${ready[0].title}` : `Fixing ${ready.length} problem links`, ready.length === 1 ? ready[0].title : `${ready.length} ${TYPES.find(([k]) => k === type)[1]} problems`);
      setFixed((s) => new Set([...s, ...ready.map((p) => p.id)]));
      setSelected(new Set());
    } catch (e) {
      toast.error(e.message);
    }
  };

  const dismiss = async (p) => {
    if (!(await confirm({ title: p.type === "hidden_lender" ? "Keep as is?" : "Not a problem?", ok: "Dismiss", body: <p>“{p.title}” will stop appearing here. This is recorded in history.</p> }))) return;
    const res = await authFetch(`${API}/problem-links/dismiss`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ problem_id: p.id, title: p.title }) });
    if (res.ok) { toast.success("Dismissed"); setData((d) => ({ ...d, problems: d.problems.filter((x) => x.id !== p.id), counts: { ...d.counts, [p.type]: d.counts[p.type] - 1 } })); }
    else toast.error("Could not dismiss");
  };

  return (
    <div className={`flex min-h-screen ${isDarkMode ? "bg-black" : "bg-slate-50"}`}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isAdmin={true} />
      <main className="flex-1 min-w-0 md:ml-64">
        <header className={`sticky top-0 z-30 px-4 md:px-6 py-4 border-b ${isDarkMode ? "bg-black border-[#1a3d18]" : "header-glass"}`}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="md:hidden p-2 rounded-lg hover:bg-slate-100"><Menu className="h-5 w-5" /></button>
              <div>
                <h1 className={`text-xl md:text-2xl font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`} data-testid="problem-page-title">Problem Links</h1>
                <p className="text-sm text-slate-500">Client links that need a look. Nothing changes until you apply a fix and confirm.</p>
              </div>
            </div>
            <Button variant="outline" onClick={load} disabled={!data} data-testid="problem-recheck-btn"><RefreshCw className={`h-4 w-4 mr-2 ${!data ? "animate-spin" : ""}`} />Re-check</Button>
          </div>
          <AffiliateNav />
        </header>
        <div className="p-4 md:p-6 space-y-4">
          <div className="flex flex-wrap gap-2" data-testid="problem-type-chips">
            {TYPES.map(([k, l]) => (
              <button key={k} onClick={() => { typePicked.current = true; setType(k); setSelected(new Set()); setLimit(50); }} data-testid={`problem-chip-${k}`}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${type === k ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-700 hover:bg-amber-50"}`}>
                {l} ({data ? data.counts[k] || 0 : "…"})
              </button>
            ))}
          </div>
          <p className="text-sm text-slate-500">{TYPES.find(([k]) => k === type)[2]}{data?.dismissed ? ` · ${data.dismissed} dismissed` : ""}</p>
          {selected.size > 0 && (
            <div className="flex items-center gap-3 rounded-lg bg-amber-100 border border-amber-300 px-3 py-2 text-sm" data-testid="problem-bulk-bar">
              <b>{selected.size} selected</b>
              <button className="underline" onClick={() => setSelected(new Set())}>Clear</button>
              <Button size="sm" className="ml-auto" onClick={() => apply(list.filter((p) => selected.has(p.id)))} data-testid="problem-apply-selected">Apply selected</Button>
            </div>
          )}
          {!data ? <p className="text-slate-500 text-sm">Checking every client link…</p>
            : list.length === 0 ? <p className="text-emerald-700 text-sm flex items-center gap-2" data-testid="problem-empty"><CheckCircle2 className="h-4 w-4" />No problems of this type.</p>
            : (
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={list.slice(0, limit).every((p) => selected.has(p.id) || fixed.has(p.id))}
                  onCheckedChange={(v) => setSelected(v ? new Set(list.slice(0, limit).filter((p) => !fixed.has(p.id)).map((p) => p.id)) : new Set())} data-testid="problem-select-all" />Select all listed</label>
                {list.slice(0, limit).map((p) => (
                  <ProblemCard key={p.id} p={p} choice={choices[p.id]} setChoice={(c) => setChoices((s) => ({ ...s, [p.id]: c }))}
                    checked={selected.has(p.id)} onCheck={(v) => setSelected((s) => { const n = new Set(s); v ? n.add(p.id) : n.delete(p.id); return n; })}
                    onApply={() => apply([p])} onDismiss={() => dismiss(p)} onOpenPerson={setPerson} fixed={fixed.has(p.id)} />
                ))}
                {list.length > limit && <Button variant="outline" onClick={() => setLimit((l) => l + 50)} data-testid="problem-show-more">Show more ({list.length - limit} left)</Button>}
              </div>
            )}
        </div>
      </main>
      <LinkedClientsDialog person={person} onClose={() => setPerson(null)} />
      {confirmDialog}
    </div>
  );
}
